// Import
import i18n from '@/i18n'
import Vue from 'vue'
import store from '@/store'
import { Api } from '@/core/api.js'

import { FORMAT, TOOLS } from '@/constants'
import { buildRadarModuleState } from '@/store/utils/radarKpis.js'
import { buildHierarchicalOrderedItems, getBaseOrder, getItemsBySection as getItemsBySectionFromItems } from '@/store/utils/checklistItems'
import { postTaskWithRetry, shouldQueueOfflineSync } from '@/utils/taskApiRetry'

const api = new Api()
import { db_issues, db_templates, db_issues_queue } from '@/database'

// Triquiñuela para resetear el state
const getDefaultState = () => {
    return {
        stats: {
            alerts: {},
            activity: {}
        },
        items: {},
        allItems: {},
        templates: {},
        templatesLoaded: false, // needed by Radar
        categories: {},
        date: false,
        type: 4,
        numberOpenIssues: {},
        config: {
            date_format: 'YYYY-MM-DD',
            hour_format: 'HH:mm',
            datetime_format: 'YYYY-MM-DD HH:mm',
            has_score: false,
            has_state: true,
            has_timing: false,
            has_mandatory: false,
            is_autosave: false,
            can_create: true,
            item_search: false,
            has_calendar: true,
            temporal_view: false,
            close_when_completed: false,
            auth_users: {
                pin: 2,
                review: 2,
                task: 1
            },
            can_edit: {
                title: true,
                state: true,
                inPast: true,
                inTime: true,
                inFuture: true
            },
            item: {
                itemsTitle: [FORMAT.TITLE, FORMAT.SUBTITLE],
                itemsImage: [FORMAT.PHOTO, FORMAT.SIGNATURE],
                itemsInfo: [FORMAT.DOCUMENT, FORMAT.MESSAGE],
                itemsExcluded: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.DOCUMENT, FORMAT.MESSAGE, FORMAT.LINK, FORMAT.RESOURCE],
                itemsVisibles: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.MESSAGE, FORMAT.LINK, FORMAT.RESOURCE],
                can_pospone: false,
                rate: false, // Esto es por si van a tener puntuaciones en funcion de lo que se responda
                can_review: true,
                has_periodicity: false,
                has_score: false
            }
        },
        radarKPIs: null
    }
}

// initial state
const state = getDefaultState()

// getters
const getters = {
    // Commons
    getAll: (state, getters, rootState) => {
        return state.items
    },
    getById: (state, getters, rootState) => (id) => {
        return id ? state.items[id] : false
    },
    getNumberOpenIssues: (state, getters, rootState) => {
        return state.numberOpenIssues
    },
    getAllItems: (state, getters, rootState) => {
        //Sort items by created_date
        let items = {}

        Object.entries(state.allItems).forEach(([key, value]) => {
            items[key] = Object.values(value).sort((a, b) => {
                return new Date(b.created_date) - new Date(a.created_date)
            })
        })

        return items
    },
    getConfig: (state, getters, rootState) => {
        return { ...state.config }
    },
    getTemplates: (state, getters, rootState) => (id) => {
        if (id) {
            return state.templates[id]
        }
        return state.templates
    },
    getTemplateById: (state) => (id) => {
        return state.templates[id]
    },
    getTemplatesLoaded: (state) => {
        return state.templatesLoaded
    },
    getTemplatesCopy: (state, getters, rootState) => {
        // if there is no 'all' template, add it
        let templates = { ...state.templates }
        if (!templates['all']) {
            templates['all'] = {
                id: 'all',
                name: i18n.t('issues.all_issues')
            }
        }
        return templates
    },
    getSubtasks: (state, getters, rootState) => (check, task) => {
        let tasks = check ? state.items[check].items : {}
        let subtask = []

        Object.keys(tasks).map(function (item_id, i) {
            var item = tasks[item_id]
            if (item.parent_id == task) {
                subtask.push(item.item_id)
            }
        })

        return subtask
    },
    getAllStats: (state, getters, rootState) => {
        return state.stats
    },
    getStats: (state, getters, rootState) => (wich) => {
        return state.stats[wich]
    },
    getCategories: (state, getters, rootState) => (id) => {
        if (id) {
            return state.categories[id]
        }
        return state.categories
    },
    getItemsBySection: (state) => (check, section) => {
        const items = check && state.items[check] ? state.items[check].items : null
        return getItemsBySectionFromItems(items, section)
    },
    getItemsWithTemporalId(state, getters, rootState) {
        return Object.values(state.items).filter((item) => item.temporal_id)
    },
    getCustomTitle: (state) => (id) => {
        return id ? state.items[id].title : false
    },

    getNotesById: (state) => (id) => {
        if (state.items[id] && state.items[id].notes) {
            return state.items[id].notes
        }
        return state.items[id].notes
    },
    getRadarModuleState: (state, getters, rootState, rootGetters) => (kpiKeys) => {
        const result = buildRadarModuleState(TOOLS.ISSUES, state.radarKPIs, kpiKeys, {}, rootGetters)

        return result
    }
}

function isRadarOpenIssueStatus(issueStates, statusId) {
    if (!issueStates || statusId == null || !issueStates[statusId]) {
        return false
    }

    const type = Number(issueStates[statusId].type)

    return !isNaN(type) && type !== 3
}

function commitUpdateOpenIssuesRadarKPIFromIssue(context, issue, delta) {
    const templateId = issue ? (issue.template_id != null ? issue.template_id : issue.template) : null

    if (templateId == null || !delta) {
        return
    }

    context.commit('updateOpenIssuesRadarKPI', { templateId, delta })
}

// actions
const actions = {
    resetState({ commit }) {
        commit('resetState')
    },
    loadNumberOpenIssues(context) {
        const allItems = context.state.allItems
        const numberOpenIssues = {}
        Object.keys(allItems).forEach((key) => {
            numberOpenIssues[key] = Object.keys(allItems[key]).length
        })

        context.commit('setNumberOpenIssues', numberOpenIssues)
    },
    loadCategories(context) {
        return api.get('checklist/4/categories').then(function (response) {
            var categories = response.data
            if (Object.values(response.data).length > 0) {
                categories['all'] = {
                    id: 'all',
                    name: i18n.t('issues.all_issues'),
                    highlight: true,
                    order: -1,
                    color: '#374673',
                    type: 3
                }
            }

            context.commit('setCategories', categories)
        })
    },
    loadAll({ state, context, commit, dispatch, rootState }, params) {
        if (fnCheckConnection()) {
            if (params.noRefresh) {
                return Promise.resolve()
            }
            return api.post('getIssues', params).then(function (response) {
                return dispatch('mergeOffline', response).then(function (response2) {
                    _.forEach(_.values(response2.data), async function (value, key) {
                        await db_issues.setItem(value.id, JSON.parse(JSON.stringify(value)))
                    })
                    dispatch('format', { data: response2.data, type: params.type })
                    dispatch('calcScore')
                    dispatch('craftAllCustomTitle')
                    return response2
                })
            })
        } else {
            var items = {}
            var count = 0
            db_issues
                .iterate(function (value, key, iterationNumber) {
                    const item = typeof value == 'string' ? JSON.parse(value) : value
                    items[item.id] = item
                    count++
                })
                .then(function () {
                    if (count > 0) {
                        dispatch('format', { data: items })
                        dispatch('calcScore')
                    }
                })
        }
    },
    addIssue(context, params) {
        var oData = { template: params.tpl_id }
        if (params.checklist_ref) {
            context.dispatch('calcScore', params.checklist_ref)

            oData.checklist_ref = params.checklist_ref

            let check = context.getters.getById(params.checklist_ref)
            let resume = { it: 0, is: 0, a: 0, n: 0 }

            for (let id in check.items) {
                let item = check.items[id]

                if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                    if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                    if (item.message) resume.n++ // if item has notes
                    if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                    if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                }
            }

            oData.resume = JSON.stringify(resume)
        }
        if (params.item_ref) {
            oData.item_ref = params.item_ref
        }

        return api.post('checklist/add', oData).then(function (response) {
            var items = { ...state.items }
            var reference = {
                checklist: Object.values(response.data)[0].id,
                type: Object.values(response.data)[0].type,
                status: Object.values(response.data)[0].status
            }
            var item = { ...items[params.checklist_ref].items[params.item_ref] }
            item.reference = reference
            items[params.checklist_ref].items[params.item_ref] = item
            Vue.set(state, 'items', items)
            return response.data
        })
    },
    async comment(context, params) {
        const canWorkOffline = context.rootGetters.getOffline

        context.dispatch('calcScore', params.check)

        let check = context.getters.getById(params.check)
        let resume = { it: 0, is: 0, a: 0, n: 1 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
            }
        }

        var sended = {
            action: 'comment',
            checklist: params.check,
            task: params.answer,
            message: params.message,
            resume: JSON.stringify(resume),
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
        }
        if (typeof params.file !== 'undefined') sended.file = params.file

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            const { response } = await postTaskWithRetry(api, 'checklist/task/comment', sended)

            if (!response.status && context.rootGetters.getOffline) {
                if (shouldQueueOfflineSync(response)) {
                    const offlineMessage = {
                        check: params.check,
                        answer: params.answer,
                        message: params.message,
                        requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
                    }
                    if (typeof params.file !== 'undefined') offlineMessage.image = params.file

                    return context
                        .dispatch('syncAction', sended)
                        .then(() => context.commit('setComment', offlineMessage))
                        .catch(() => context.commit('setComment', offlineMessage))
                }
                return context.dispatch('syncAction', sended)
            }

            if (!response.status) {
                var error = {
                    check: params.check,
                    answer: params.answer
                }
                if (typeof params.file !== 'undefined') error.image = null
                else error.message = ''

                context.commit('setComment', error)
                return { status: false }
            }

            var returned = {
                check: params.check,
                answer: params.answer,
                message: response.data.message
            }
            if (typeof response.data.image !== 'undefined') {
                returned.image = response.data.image
            }

            return context.commit('setComment', returned)
        } else if (context.rootGetters.getOffline) {
            console.error(sended)
            return context.dispatch('syncAction', sended)
        } else {
            var error = {
                check: params.check,
                answer: params.answer
            }
            if (typeof params.file !== 'undefined') error.image = null
            else error.message = ''

            context.commit('setComment', error)
            return { status: false }
        }
    },
    validate(context, params) {
        context.commit('setValidation', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.date
        })
        context.dispatch('calcScore', params.check)

        let check = context.getters.getById(params.check)
        let resume = { it: 0, is: 0, a: 0, n: 1 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
            }
        }

        return api
            .post('checklist/task/validate', {
                checklist: params.check,
                task: params.answer,
                employee: params.emp,
                complete_date: params.date,
                resume: JSON.stringify(resume)
            })
            .then(function (response) {})
    },
    unvalidate(context, params) {
        context.commit('setUnvalidation', {
            check: params.check,
            answer: params.answer
        })
        context.commit('calcScore', params.check)

        let check = context.getters.getById(params.check)
        let resume = { it: 0, is: 0, a: 0, n: 1 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
            }
        }

        return api
            .post('checklist/task/unvalidate', {
                checklist: params.check,
                task: params.answer,
                resume: JSON.stringify(resume)
            })
            .then(function (response) {})
    },
    complete(context, params) {
        context.dispatch('calcScore', params.check)

        let check = context.getters.getById(params.checklist)
        let resume = { it: 0, is: 0, a: 0, n: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
            }
        }

        return api
            .post('checklist/complete', {
                checklist: params.checklist,
                complete_date: params.complete_date,
                resume: JSON.stringify(resume)
            })
            .then(function (response) {
                context.commit('setCompleteDateItem', params.checklist, response)
                return response
            })
    },
    uncomplete(context, params) {
        return api
            .post('checklist/uncomplete', {
                checklist: params.checklist
            })
            .then(function (response) {
                return response
            })
    },
    loadById(context, params) {
        if (!fnCheckConnection()) {
            return loadDetailOffline(context, params.id)
        }

        if (params.noRefresh) {
            const item = context.getters.getById(params.id)
            context.dispatch('format', { data: item })
            context.dispatch('calcScore', params.id)
            return Promise.resolve()
        }

        if (!params.noRefresh && fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.get('checklist/id/' + params.id, {}).then(function (response) {
                context.dispatch('format', { data: response.data })
                context.dispatch('calcScore', params.id)
            })
        }
    },
    loadTemplates(context, params) {
        if (!fnCheckConnection()) {
            return loadOffline(context)
        }
        return api
            .get('checklist/templates/' + state.type, {})
            .then(function (response) {
                context.commit('setTemplates', { data: response.data })
                context.commit('setTemplatesLoaded', true)

                _.forEach(_.values(response.data), async function (value, key) {
                    await db_templates.setItem(value.id, JSON.stringify(value))
                })
            })
            .catch((error) => {
                logError(error)
                return loadOffline(context)
            })
    },
    reload() {
        store.dispatch('format', { data: checklist })
        store.dispatch('calcScore')
    },
    update(context, params) {
        var oData = { checklist: params.check }
        const currentIssue = context.state.items[params.check]
        const previousStatus = currentIssue ? currentIssue.status : null
        const issueStates = context.rootState.issue_states
        const wasOpenRadarIssue = isRadarOpenIssueStatus(issueStates, previousStatus)
        const isOpenRadarIssue = isRadarOpenIssueStatus(issueStates, params.status)
        const radarDelta = params.status && wasOpenRadarIssue !== isOpenRadarIssue ? (isOpenRadarIssue ? 1 : -1) : 0

        if (params.title) oData.title = params.title
        if (params.status) oData.status = params.status
        if (params.emp_ref) {
            oData.employee_ref = params.emp_ref
        }
        let completeParams = {}

        if (params.status) {
            let newState = issueStates && issueStates[params.status]
            const isClosedState = newState && Number(newState.type) === 3

            completeParams = {
                check: previousStatus,
                item: params.check,
                complete_date: isClosedState ? moment().format('YYYY-MM-DD HH:mm:ss') : false,
                new_status: params.status
            }
            if (isClosedState) {
                const empId = context.rootGetters['loginUser/getLocalEmployee']
                const closingEmployee = empId ? context.rootGetters['employee/getEmployee'](empId) : false
                if (closingEmployee) {
                    completeParams.employee = closingEmployee
                }
            }
            context.commit('setCompleteDateItem', completeParams)
        }

        const canWorkOffline = context.rootGetters.getOffline

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/update', oData).then(function (response) {
                if (params.title) {
                    context.commit('setChecklistProp', {
                        check: oData.checklist,
                        prop: 'title',
                        value: oData.title
                    })
                }
                if (params.status) {
                    context.commit('setChecklistProp', {
                        check: oData.checklist,
                        prop: 'status',
                        value: oData.status
                    })
                    if (response && response.status === true) {
                        commitUpdateOpenIssuesRadarKPIFromIssue(context, currentIssue, radarDelta)
                    }
                }
                if (params.emp_ref) {
                    context.commit('setChecklistProp', {
                        check: oData.checklist,
                        prop: 'employee_ref',
                        value: oData.employee_ref
                    })
                }
            })
        } else {
            if (canWorkOffline) {
                const oldState = context.state.items[params.check].status
                oData.action = 'update'
                if (params.title) {
                    context.commit('setChecklistProp', {
                        check: oData.checklist,
                        prop: 'title',
                        value: oData.title
                    })
                }
                if (params.status) {
                    context.commit('setChecklistProp', {
                        check: oData.checklist,
                        prop: 'status',
                        value: oData.status
                    })
                    commitUpdateOpenIssuesRadarKPIFromIssue(context, currentIssue, radarDelta)
                }
                if (params.emp_ref) {
                    context.commit('setChecklistProp', {
                        check: oData.checklist,
                        prop: 'employee_ref',
                        value: oData.employee_ref
                    })
                }

                //Update allItems
                const newIssueItem = context.state.items[params.check]
                //delete allItems[oldState][params.check]
                delete context.state.allItems[oldState][params.check]
                context.state.allItems[newIssueItem.status][params.check] = { ...newIssueItem }

                context
                    .dispatch('syncAction', oData)
                    .then(async () => {
                        await db_issues.iterate(async function (value, key, iterationNumber) {
                            const item = typeof value == 'string' ? JSON.parse(value) : value
                            if (item.id == params.check) {
                                if (params.title) item.title = params.title
                                if (params.status) item.status = params.status
                                if (params.emp_ref) item.employee_ref = params.emp_ref
                                if (completeParams.complete_date) item.complete_date = completeParams.complete_date
                                if (completeParams.employee) item.employee = completeParams.employee
                                await db_issues.setItem(item.id, JSON.parse(JSON.stringify(item)))
                            }
                        })
                        const items = context.state.items
                        context.dispatch('format', { data: items })
                    })
                    .catch(async () => {
                        await db_issues.iterate(async function (value, key, iterationNumber) {
                            const item = typeof value == 'string' ? JSON.parse(value) : value
                            if (item.id == params.check) {
                                if (params.title) item.title = params.title
                                if (params.status) item.status = params.status
                                if (params.emp_ref) item.employee_ref = params.emp_ref
                                if (completeParams.complete_date) item.complete_date = completeParams.complete_date
                                if (completeParams.employee) item.employee = completeParams.employee
                                await db_issues.setItem(item.id, JSON.parse(JSON.stringify(item)))
                            }
                        })
                        const items = context.state.items
                        context.dispatch('format', { data: items })
                    })
            }
        }
    },
    async craftCustomTitle(context, params) {
        const checklistId = params && params.check
        const checklist = context.getters.getById(checklistId)
        const templateId = checklist && checklist.template_id
        const template = context.getters.getTemplates(templateId)

        // items del checklist
        let items = Object.entries(checklist.items)
        items.sort(([, a], [, b]) => a.order - b.order)

        // Crear título
        let customtitle = ''

        items.forEach(([key, item]) => {
            if (item.config && item.config.custom_title && item.value) {
                switch (item.type) {
                    case 4:
                    case 6:
                    case 11:
                        customtitle += item.value + ' - '
                        break
                    case 15:
                        if (item.config.units) {
                            customtitle += item.value + item.config.units + ' - '
                        } else {
                            customtitle += item.value + 'º - '
                        }
                        break
                }
            }
        })

        if (template) {
            const templateName = template && template.name
            const checklistStatus = checklist && checklist.status

            // setear título
            if (customtitle.length > 0) {
                context.commit('setTitle', {
                    check: checklistId,
                    title: customtitle + templateName,
                    status: checklistStatus
                })
            } else {
                context.commit('setTitle', {
                    check: checklistId,
                    title: templateName,
                    status: checklistStatus
                })
            }
        }
    },
    async craftAllCustomTitle(context) {
        const items = context.getters.getAll

        Object.keys(items).forEach((key) => {
            context.dispatch('craftCustomTitle', { check: key })
        })
    },
    async answer(context, params) {
        let value = params.value
        let subtask = context.getters.getSubtasks(params.check, params.answer)
        const canWorkOffline = context.rootGetters.getOffline
        // In case of image or signature type, JSON-parse the value
        if (state.config.item.itemsImage.includes(params.answer_type)) {
            value = JSON.stringify(value)
        }

        if (params.value == '' && params.answer_type == 4) {
            context.commit('issues/reset', {
                check: this.cId,
                answer: this.aId
            })
            return { status: false }
        }

        context.commit('setAnswer', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.date,
            value: params.value,
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline,
            bReadingFromSensor: params.bReadingFromSensor
        })
        await context.dispatch('calcScore', params.check)
        let check = context.getters.getById(params.check)
        let resume = { it: 0, is: 0, a: 0, n: 0 }
        for (let id in check.items) {
            let item = check.items[id]
            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
            }
        }

        const paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            value: value,
            resume: JSON.stringify(resume),
            type: params.answer_type,
            bReadingFromSensor: params.bReadingFromSensor
            // customTitleEnabled: params.customTitle //
        }

        if (canWorkOffline) {
            await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'answer' })
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            const { response } = await postTaskWithRetry(api, 'checklist/task/answer', paramsApi)

            if (!response.status) {
                if (canWorkOffline && shouldQueueOfflineSync(response)) {
                    paramsApi.action = 'answer'
                    try {
                        await context.dispatch('syncAction', paramsApi)
                    } catch (error) {
                        logError(error)
                    }
                    return context.dispatch('calcScore', params.check).then(function () {
                        return { status: false }
                    })
                }

                context.commit('setReset', { check: params.check, answer: params.answer })
                return context.dispatch('calcScore', params.check).then(function () {
                    if (response.msg && response.msg === 'Error: Api Error') {
                        return { status: false, msg: 'Error: Api Error' }
                    }
                    return { status: false }
                })
            }

            let value = response.data.value
            if (state.config.item.itemsImage.includes(params.answer_type)) {
                value = splitCommasBase64(value)
                context.commit('setAnswer', {
                    check: params.check,
                    answer: params.answer,
                    emp: params.emp,
                    date: params.date,
                    value: value,
                    bReadingFromSensor: params.bReadingFromSensor
                })
            }

            let title = response.data.customized_title ? response.data.customized_title : false
            if (title) {
                context.dispatch('craftCustomTitle', { check: params.check })
            }

            return response
        } else {
            if (canWorkOffline) {
                paramsApi.action = 'answer'
                const checklist = context.getters.getById(params.check)
                if (checklist.toolQueue) {
                    paramsApi.toolQueue = checklist.toolQueue
                }

                context.dispatch('craftCustomTitle', params)

                return context
                    .dispatch('syncAction', paramsApi)
                    .then(() => {
                        return context.dispatch('calcScore', params.check).then(function () {
                            return { status: false }
                        })
                    })
                    .catch(() => {
                        return context.dispatch('calcScore', params.check).then(function () {
                            return { status: false }
                        })
                    })
            } else {
                context.commit('setReset', { check: params.check, answer: params.answer })
                return context.dispatch('calcScore', params.check).then(function () {
                    return { status: false }
                })
            }
        }
    },
    async apply(context, params) {
        let subtask = context.getters.getSubtasks(params.check, params.answer)
        let item = state.items[params.check].items[params.answer]
        let items = context.getters.getItemsBySection(params.check, params.answer)

        context.commit('setApply', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.date == 0 ? params.date : false,
            apply: params.apply
        })

        if (item.type == FORMAT.TITLE) {
            Object.keys(items).map(function (item_id, i) {
                var it = items[item_id]
                if (!it.mandatory) {
                    context.commit('setApply', { check: params.check, answer: it.item_id, date: params.apply == 0 ? params.date : false, apply: params.apply })
                }
            })
        }

        await context.dispatch('calcScore', params.check)

        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                var item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        let check = context.getters.getById(params.check)
        let resume = { it: 0, is: 0, a: 0, n: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
            }
        }

        let paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            apply: params.apply,
            resume: JSON.stringify(resume)
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api
                .post('checklist/task/apply', paramsApi)
                .then(function (response) {
                    if (!response.status && context.rootGetters.getOffline) {
                        paramsApi.action = 'apply'
                        return context.dispatch('syncAction', paramsApi)
                    } else {
                        context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
                    }
                })
                .catch(() => {
                    if (context.rootGetters.getOffline) {
                        paramsApi.action = 'apply'
                        return context.dispatch('syncAction', paramsApi)
                    } else {
                        context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
                    }
                })
        } else {
            if (context.rootGetters.getOffline) {
                paramsApi.action = 'apply'
                return context.dispatch('syncAction', paramsApi)
            } else {
                context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
            }
        }
    },
    async applyrecurrent(context, params) {
        let subtask = context.getters.getSubtasks(params.check, params.answer)

        // apply
        context.commit('setApply', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.apply == 0 ? params.date : false,
            apply: params.apply
        })

        var item = state.items[params.check].items[params.answer]
        if (item.type == FORMAT.TITLE) {
            let items = context.getters.getItemsBySection(params.check, params.answer)

            Object.keys(items).map(function (item_id, i) {
                var it = items[item_id]
                if (!it.mandatory) {
                    context.commit('setApply', { check: params.check, answer: it.item_id, date: params.apply == 0 ? params.date : false, apply: params.apply })
                }
            })
        }

        // calculate score
        await context.dispatch('calcScore', params.check)

        // reset child items
        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                var item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        // calculate score

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
            }
        }

        return api
            .post('checklist/task/applyrecurrent', {
                checklist: params.check,
                task: params.answer,
                employee: params.emp,
                complete_date: params.date,
                apply: params.apply,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume)
            })
            .then(function (response) {
                if (!response.status) {
                    context.commit('setReset', { check: params.check, answer: params.answer })
                }
            })
    },
    async reset(context, params) {
        const canWorkOffline = context.rootGetters.getOffline
        if (context.rootGetters.getOffline || fnCheckConnection()) {
            let subtask = context.getters.getSubtasks(params.check, params.answer)

            context.commit('setReset', {
                check: params.check,
                answer: params.answer,
                emp: params.emp,
                date: params.date
            })
            await context.dispatch('calcScore', params.check)

            if (subtask.length > 0) {
                Object.keys(subtask).map(function (item_id, i) {
                    var item = subtask[item_id]
                    context.commit('setReset', { check: params.check, answer: item })
                })
            }

            let check = context.getters.getById(params.check)
            let total = check.total
            let answered = check.completed
            let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
            let resume = { it: 0, is: 0, a: 0, n: 0 }

            for (let id in check.items) {
                let item = check.items[id]

                if (item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                    if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                    if (item.message) resume.n++ // if item has notes
                    if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                    if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                }
            }

            let paramsApi = {
                checklist: params.check,
                task: params.answer,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume)
            }

            if (canWorkOffline) {
                await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'reset' })
            }

            if (fnCheckConnection() && context.rootGetters.getFastConnection) {
                const { response } = await postTaskWithRetry(api, 'checklist/task/reset', paramsApi)

                let title = false
                if (response.customized_title) {
                    title = response.customized_title ? response.customized_title : false
                } else if (response.title) {
                    title = response.title ? response.title : false
                }
                if (title) {
                    context.dispatch('craftCustomTitle', { check: params.check })
                }

                if (!response.status) {
                    paramsApi.action = 'reset'
                    try {
                        await context.dispatch('syncAction', paramsApi)
                    } catch (error) {
                        console.error('Error in reset():', error)
                    }
                }

                return { status: true }
            } else {
                paramsApi.action = 'reset'
                context.dispatch('craftCustomTitle', params)
                return context
                    .dispatch('syncAction', paramsApi)
                    .then(function () {
                        return { status: true }
                    })
                    .catch(() => {
                        return { status: true }
                    })
            }
        } else {
            return { status: false }
        }
    },
    async add(context, params) {
        var type = context.state.type
        var oData = { template: params.tpl_id }
        if (params.emp_ref) {
            oData.employee_ref = params.emp_ref
        }
        if (params.checklist_ref) {
            oData.checklist_ref = params.checklist_ref
        }
        if (params.item_ref) {
            oData.item_ref = params.item_ref
        }

        /*  await context.dispatch('calcScore', params.checklist_ref)

        let check = context.getters.getById(params.checklist_ref) */
        let resume = { it: 0, is: 0, a: 0, n: 0 }

        /* for (let id in check.items) {
            let item = check.items[id]

            if (item.is_visible) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
            }
        } */

        oData.resume = JSON.stringify(resume)
        const canWorkOffline = context.rootGetters.getOffline
        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            context.commit('resetAllItems')
            return api
                .post('checklist/add', oData)
                .then(function (response) {
                    const createdIssueId = Object.keys(response.data)[0]
                    const createdIssue = response.data[createdIssueId]

                    context.dispatch('format', { data: response.data })
                    context.dispatch('calcScore', createdIssueId)
                    if (isRadarOpenIssueStatus(context.rootState.issue_states, createdIssue.status)) {
                        commitUpdateOpenIssuesRadarKPIFromIssue(context, createdIssue, 1)
                    }
                    return createdIssue
                })
                .catch(() => {
                    //POR SI OCURRE ALGUN FALLO (EJ. ENVIAR UN MAIL CON VARIABLES EQUIVOCADAS)
                })
        } else {
            if (canWorkOffline) {
                if (params.isOfflineCreated) params.tpl_id = params.template
                const items = context.state.templates[params.tpl_id].items
                const itemsWithTempIds = items.map((item) => {
                    if (parseInt(item.type) === FORMAT.MULTIPLE && item.config && item.config.values) {
                        item.config.values = item.config.values.map((option) => {
                            if (option.c) {
                                return { v: option.v, c: option.c }
                            } else if (option.v && !option.c) {
                                return { v: option.v, c: false }
                            } else {
                                return { v: option, c: false }
                            }
                        })
                    }
                    return {
                        ...item,
                        type: parseInt(item.type),
                        item_id: item.id,
                        complete_date: false,
                        value: null,
                        review: false,
                        alerts: items.alerts ? items.alerts : false,
                        alerts_triggers: items.alerts_triggers ? items.alerts_triggers : false,
                        parent_id: item.parent_id ? item.parent_id : false,
                        apply: item.apply ? item.apply : '1', //"1"
                        reference: false,
                        conditions: false,
                        employee: false,
                        image: false,
                        is_visible: true,
                        mandatory: item.mandatory ? parseInt(item.mandatory) : false,
                        margin: 0,
                        message: false,
                        parent_id: false,
                        periodicity: 1,
                        reference: false,
                        require_pin: false,
                        //require_pin_review:false,
                        require_review: false,
                        resource: false
                    }
                })

                const oTemplateItems = {}
                itemsWithTempIds.forEach((item) => {
                    oTemplateItems[item.id] = item
                })

                const issueStatusId = Object.entries(store.getters['getIssueStates'])
                    .filter(([key, value]) => value.type === 1)
                    .map(([key, value]) => value.id)

                let reference = false
                const tool = {
                    tasks: '1',
                    register: '3',
                    audit: '2',
                    issues: '4'
                }

                if (params.checklist_ref && params.item_ref && params.toolQueue) {
                    reference = {
                        ref: {
                            checklist: params.checklist_ref,
                            item: params.item_ref,
                            type: tool[params.toolQueue]
                        }
                    }
                }

                const newIssues = {
                    business_date: moment(),
                    category_id: false,
                    complete_date: false,
                    completed: false,
                    config: context.state.templates[params.tpl_id].config,
                    created_date: moment(),
                    data: reference,
                    def: context.state.config,
                    default: false,
                    description: context.state.templates[params.tpl_id].description,
                    employee: store.getters['loginUser/getUserInformation'],
                    employee_ref: store.getters['loginUser/getUserInformation'],
                    employees: [],
                    id: 'tmpitw' + Math.floor(Date.now() / 1000),
                    issue_id: null,
                    is_visible: true,
                    items: oTemplateItems,
                    justify_id: false,
                    recover: false,
                    score: 0,
                    status: issueStatusId[0],
                    template: params.tpl_id,
                    template_id: params.tpl_id,
                    title: context.state.templates[params.tpl_id].name,
                    total: items.length,
                    type: context.state.templates[params.tpl_id].type,
                    unanswered: items.length,
                    requireSync: true
                    //uncompleted: 0
                }

                const syncData = {
                    action: 'add',
                    checklist: newIssues.id,
                    employee: newIssues.employee.id,
                    template_ref: params.tpl_id,
                    type: newIssues.type,
                    issue_id: newIssues.issue_id,
                    issue_status: newIssues.status
                }

                if (params.isOfflineCreated) {
                    syncData.item_ref = params.item_ref
                    syncData.checklist_ref = params.checklist_ref
                }

                if (params.toolQueue) {
                    syncData.toolQueue = params.toolQueue
                    newIssues['toolQueue'] = params.toolQueue
                }

                store.dispatch('issues/syncAction', syncData)
                const allItems = { [newIssues.id]: newIssues, ...context.state.items }
                await db_issues.setItem(newIssues.id, JSON.parse(JSON.stringify(newIssues)))

                context.dispatch('format', { data: allItems })
                //allItems variable update
                const newIssueItem = context.state.items[newIssues.id]
                context.state.allItems[newIssues.status] = { [newIssues.id]: newIssueItem, ...context.state.allItems[newIssues.status] }
                if (isRadarOpenIssueStatus(context.rootState.issue_states, newIssues.status)) {
                    commitUpdateOpenIssuesRadarKPIFromIssue(context, newIssues, 1)
                }

                return newIssues
            }
        }
    },
    delete(context, params) {
        const issue = context.state.items[params.check]
        const shouldUpdateOpenIssuesRadarKPI = issue && isRadarOpenIssueStatus(context.rootState.issue_states, issue.status)
        var oData = {
            checklist: params.check,
            employee: params.emp,
            status: -1
        }
        if (params.ref_item) {
            oData['ref_item'] = params.ref_item
        }

        db_issues.removeItem(params.check)

        return api.post('checklist/update', oData).then(function (response) {
            if (response && response.status !== false && shouldUpdateOpenIssuesRadarKPI) {
                commitUpdateOpenIssuesRadarKPIFromIssue(context, issue, -1)
            }
            return response
        })
    },
    loadStats(context, params) {
        context.commit('resetStats', {})
        let config = state.config
        var currentDate = moment(params.date)
        var items = state.items
        var states = params.states
        var left_states = {}
        var new_issues = 0

        // SET ALERTS --------------------------------------------------------------
        // --> Left state
        // Si tiene ESTADOS hacer la alerta, sino no
        if (config.has_state && states) {
            let limitDate = moment(currentDate).subtract(30, 'days')
            let state_searched = []

            // Get searched states
            Object.keys(states).map(function (state_id, i) {
                var state = states[state_id]

                if (state.alert) {
                    state_searched.push(state.id)
                    left_states[state.id] = { value: 0, name: state.name }
                }
            })

            // Search issues with those states
            Object.keys(items).map(function (item_id, i) {
                var item = items[item_id]

                if (state_searched.includes(item.status) && moment(item.business_date).isBetween(limitDate, currentDate, 'day', [])) {
                    left_states[item.status].value++
                }
            })

            Object.keys(left_states).map(function (item_id, i) {
                var st = left_states[item_id]

                if (parseInt(st.value) > 0) {
                    st.state = 'info'
                    st.name = i18n.tc('home.issues.left_state', st.value) + " '" + st.name + "'"
                    st.route = 'Issues'
                    context.commit('setStats', {
                        where: 'alerts',
                        key_name: st.name.trim().toLowerCase(),
                        values: st
                    })
                }
            })
        }

        // SET ACTIVITY ------------------------------------------------------------

        // -- Created Issues
        Object.keys(items).map(function (item_id, i) {
            var item = items[item_id]

            if (item.business_date == currentDate.format('YYYY-MM-DD')) {
                new_issues++
            }
        })

        if (new_issues > 0) {
            context.commit('setStats', {
                where: 'activity',
                key_name: 'created',
                values: {
                    name: i18n.tc('home.activity.issues_generated', new_issues),
                    value: new_issues
                }
            })
        }
    },
    format({ state, context, commit, dispatch, rootState }, params = {}) {
        // var append = typeof payload.append != "undefined" ? payload.append : false;

        var aItems = {}
        // if (append) {
        //   var aItems = state.items;
        // }

        if (!params || !params.data) {
            return
        }

        Object.keys(params.data).map(function (item_id, i) {
            var list = params.data[item_id]

            // Timezone set
            if (typeof list.business_date === 'string') {
                list.business_date = moment(list.business_date)
            } else {
                list.business_date = list.business_date ? (moment.isMoment(list.business_date) ? list.business_date : moment(list.business_date * 1000)) : false
            }

            if (typeof list.created_date === 'string') {
                list.created_date = moment(list.created_date)
            } else {
                list.created_date = list.created_date ? (moment.isMoment(list.created_date) ? list.created_date : moment(list.created_date * 1000)) : false
            }

            if (typeof list.complete_date === 'string') {
                list.complete_date = moment(list.complete_date)
            } else {
                list.complete_date = list.complete_date ? (moment.isMoment(list.complete_date) ? list.complete_date : moment(list.complete_date * 1000)) : false
            }

            list.def = state.config
            list.is_visible = true

            // Loop for all items
            Object.keys(list.items).map(function (item_id, i) {
                var item = list.items[item_id]

                // Timezone set
                item.complete_date = item.complete_date ? (moment.isMoment(item.complete_date) ? item.complete_date : moment(item.complete_date * 1000)) : false
                if (item.review) {
                    item.review['complete_date'] = item.review.complete_date ? (moment.isMoment(item.review.complete_date) ? item.review.complete_date : moment(item.review.complete_date * 1000)) : false
                }

                // If type image - Split value to array
                if (state.config.item.itemsImage.includes(item.type)) {
                    item.value = item.value && typeof item.value === 'string' ? splitCommasBase64(item.value) : item.value
                }

                item.config = typeof item.config === 'string' ? JSON.parse(item.config) : item.config

                list.items[item_id] = { ...item }
                Vue.set(list.items, item_id, { ...item })
            })

            aItems[list.id] = list
        })
        commit('setAllItems', { items: aItems, type: params.type })
        commit('setFormat', { items: aItems })
    },
    calcScore({ state, context, commit, dispatch, rootState }, id) {
        const checklists = state.items

        Object.keys(checklists).map((incidentId, i) => {
            const checklist = checklists[incidentId]

            if (typeof id === 'undefined' || incidentId == id) {
                // commented out because it's not used
                // var past_score = audit.score ? audit.score : 0

                checklist.total = 0
                checklist.completed = 0

                if (typeof id !== 'undefined') {
                    checklist.def = state.config
                    checklist.uncompleted = 0
                    // checklist.completed = 0
                    checklist.unanswered = 0
                    // checklist.total = 0
                    checklist.score = 0
                    checklist.justify_id = checklist.justify_id ? checklist.justify_id : checklist.justify_msg ? '0' : false
                }

                // Loop for all items
                Object.keys(checklist.items).map((itemId, i) => {
                    const item = checklist.items[itemId]

                    item.is_visible = true
                    item.alerts = false
                    // Subsubitem: item whose parent is itself a subitem (parent has parent_id)
                    const parent = item.parent_id ? checklist.items[item.parent_id] : null
                    item.is_subsubitem = !!(parent && parent.parent_id)

                    // SUBITEMS
                    if (typeof item.conditions !== 'undefined' && item.conditions != null && item.conditions) {
                        const subitemConditionsData = item.conditions
                        const conditionsCombinator = typeof subitemConditionsData.operator !== 'undefined' ? subitemConditionsData.operator : 'AND'

                        const conditions = subitemConditionsData.values

                        let shouldDisplay = null
                        let parentItemOrder = item.parent_id && checklist.items[item.parent_id] ? parseFloat(checklist.items[item.parent_id].order) : -1

                        // condition key === subitem parent_id and parent_id === parent item item_id
                        for (const key in conditions) {
                            let show = false
                            item.is_visible = false

                            const condition = conditions[key]
                            const conditionOperator = condition && typeof condition.operator !== 'undefined' ? condition.operator : '='
                            const conditionValue = condition ? condition.value : false

                            const parentConditionItem = checklist.items[key]
                            const parentItem = parentConditionItem || null
                            const parentItemValue = parentItem && typeof parentItem.value !== 'undefined' ? parentItem.value : null

                            if (parentConditionItem && (parentItemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(parentConditionItem.type)) && parentConditionItem.is_visible) {
                                if (conditionOperator == 'true' && conditionValue == 'true') {
                                    show = true
                                } else {
                                    // Get task type from parent
                                    switch (parentConditionItem.type) {
                                        //   switch (item.type) {
                                        case FORMAT.CHECK:
                                            show = compare(conditionValue, parentItemValue, conditionOperator)
                                            break
                                        case FORMAT.EMAIL:
                                        case FORMAT.DOCUMENT:
                                        case FORMAT.YESNO:
                                            show = compare(parseInt(conditionValue) == 1 || (conditionValue == '' && conditionOperator == '!empty'), parentItemValue == true || parentItemValue == 'true' || (parentItemValue != null && conditionValue == '' && conditionOperator == '!empty'), conditionOperator)
                                            break
                                        case FORMAT.YESNONC:
                                            show = compare(parseInt(conditionValue) || (conditionValue === '' && conditionOperator === '!empty'), parentItemValue || (conditionValue === '' && conditionOperator === '!empty'), conditionOperator)
                                            break
                                        case FORMAT.MULTIPLE:
                                            const parentItemIsMultiselect = parentItem && parentItem.config && parentItem.config.hasOwnProperty('multiselect') && parentItem.config.multiselect === 1

                                            if (!parentItemIsMultiselect) {
                                                // SINGLE SELECT
                                                show = compare(conditionValue || (conditionValue === '' && conditionOperator === '!empty'), parentItemValue, conditionOperator)
                                            } else {
                                                // MULTIPLE SELECT
                                                if (Array.isArray(parentItemValue) || (typeof parentItemValue === 'string' && parentItemValue.includes('|'))) {
                                                    const parentItemValues = Array.isArray(parentItemValue) ? parentItemValue : parentItemValue.split('|')
                                                    show = parentItemValues.some((value) => compare(conditionValue, value, conditionOperator))
                                                } else {
                                                    show = compare(conditionValue, parentItemValue, conditionOperator)
                                                }
                                            }
                                            break
                                        case FORMAT.TEXT:
                                        case FORMAT.TEXTAREA:
                                        case FORMAT.PHOTO:
                                        case FORMAT.DATE:
                                        case FORMAT.TIME:
                                        case FORMAT.DATETIME:
                                        case FORMAT.SIGNATURE:
                                            show = compare(conditionValue, parentItemValue, conditionOperator)
                                            break
                                        case FORMAT.NUMBER:
                                            var aux = parentItemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), conditionValue, conditionOperator) || (conditionValue == '' && conditionOperator == '!empty')
                                            break
                                        case FORMAT.TEMPERATURE:
                                            var aux = parentItemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), conditionValue, conditionOperator) || (conditionValue == '' && conditionOperator == '!empty')
                                            break
                                        default:
                                            break
                                    }
                                }
                            }
                            if (shouldDisplay == null) shouldDisplay = show
                            else {
                                switch (conditionsCombinator) {
                                    case 'AND':
                                        shouldDisplay = shouldDisplay && show
                                        break
                                    case 'OR':
                                        shouldDisplay = shouldDisplay || show
                                        break
                                }
                            }

                            // if (show) {
                            //     itemOrder = checklist.items[key].order
                            // }

                            if (parentItemOrder === -1 && parentConditionItem) {
                                parentItemOrder = parseFloat(parentConditionItem.order)
                            }
                        }

                        item.is_visible = shouldDisplay
                        // item.order = parseInt(parentItemOrder) + 0.5

                        const subitemOrder = getBaseOrder(item)
                        if (!Number.isNaN(parentItemOrder) && parentItemOrder !== -1 && !Number.isNaN(subitemOrder)) {
                            item.order = parentItemOrder + subitemOrder / 10000
                        } else {
                            item.order = subitemOrder
                        }
                    }

                    // Alerts TODO
                    if (item.alerts_triggers) {
                        const alerts = item.alerts_triggers
                        const itemValue = item.value

                        let oAlert = {}
                        let message = ''
                        let preview = ''
                        let issues = []
                        let classes = []

                        // SHOW ALERTS
                        for (const i in alerts) {
                            const alert = alerts[i]
                            const operator = alert.operator ? alert.operator : '='
                            const alertValue = alert.value

                            let show = false

                            if ((itemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(item.type)) && item.is_visible) {
                                if (operator == 'true' && alertValue == 'true') {
                                    show = true
                                } else {
                                    switch (item.type) {
                                        case FORMAT.CHECK:
                                            show = compare(alertValue, itemValue, operator)
                                            break
                                        case FORMAT.EMAIL:
                                        case FORMAT.DOCUMENT:
                                        case FORMAT.YESNO:
                                            show = compare(parseInt(alertValue) == 1 || (alertValue == '' && operator == '!empty'), itemValue == true || itemValue == 'true', operator)
                                            break
                                        case FORMAT.YESNONC:
                                            show = compare(parseInt(alertValue) || (alertValue == '' && operator == '!empty'), itemValue, operator)
                                            break
                                        case FORMAT.MULTIPLE:
                                            const itemIsMultiselect = item && item.config && item.config.hasOwnProperty('multiselect') && item.config.multiselect === 1

                                            // MULTIPLE SELECT
                                            if (itemIsMultiselect && (Array.isArray(itemValue) || (typeof itemValue === 'string' && itemValue.includes('|')))) {
                                                const itemValues = Array.isArray(itemValue) ? itemValue : itemValue.split('|')

                                                show = itemValues.some((value) => compare(alertValue || (alertValue === '' && operator === '!empty'), value, operator))
                                            } else {
                                                // SINGLE SELECT
                                                show = compare(alertValue || (alertValue === '' && operator === '!empty'), itemValue, operator)
                                            }
                                            break
                                        case FORMAT.TEXT:
                                        case FORMAT.TEXTAREA:
                                        case FORMAT.PHOTO:
                                        case FORMAT.DATE:
                                        case FORMAT.TIME:
                                        case FORMAT.DATETIME:
                                        case FORMAT.SIGNATURE:
                                            show = compare(alertValue, itemValue, operator)
                                            break
                                        case FORMAT.NUMBER:
                                            var aux = itemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), alertValue, operator) || (itemValue != '-' && operator == '!empty')
                                            break
                                        case FORMAT.TEMPERATURE:
                                            var aux = itemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), alertValue, operator) || (itemValue != '-' && operator == '!empty')
                                            break
                                        default:
                                            break
                                    }
                                }
                            }

                            if (show && alert.msg) {
                                if (message == '') message = alert.msg
                                else message = message + '<br/>' + alert.msg

                                if (preview == '') preview = alert.msg
                                else preview = preview + ', ' + alert.msg
                            }

                            if (show && alert.tpl) {
                                issues.push(alert.tpl)
                            }
                            if (show && alert.class) {
                                if (alert.class == 'alert') {
                                    alert.class = 'warning'
                                }
                                classes.push(alert.class)
                            }
                        }

                        oAlert = {
                            preview: preview,
                            message: message,
                            issues: issues,
                            class: classes
                        }

                        if (oAlert.message.length > 0 || oAlert.issues.length > 0) {
                            item.alerts = oAlert
                            Vue.set(item, 'alerts', oAlert)
                        }
                    }

                    // Scoring
                    if (((!item.parent_id && checklist.def.item.itemsExcluded.indexOf(item.type) == -1) || (item.parent_id && item.is_visible)) && !item.planned_to_date) {
                        switch (item.type) {
                            case FORMAT.YESNO: // SI/NO
                            case FORMAT.YESNONC: // SI/NO/NSNC
                            case FORMAT.CHECK: // CHECKS
                            case FORMAT.TEXT: // TEXTO
                            case FORMAT.PHOTO: // FOTO
                            case FORMAT.NUMBER: // NUMBER
                            case FORMAT.DATE: // DATE
                            case FORMAT.TEXTAREA: // TEXTAREA
                            case FORMAT.MULTIPLE: // MULTIPLE SELECT
                            case FORMAT.TIME: // TIME
                            case FORMAT.DATETIME: // DATETIME
                            case FORMAT.SIGNATURE: // SIGNATURE
                            case FORMAT.TEMPERATURE: // TEMPERATURE
                            case FORMAT.EMAIL: // EMAIL
                            case FORMAT.DOCUMENT: // DOCUMENT
                                if (item.complete_date == false && item.apply) checklist.unanswered++
                                else {
                                    // Required to be reviewed
                                    if (item.require_review) {
                                        checklist.completed += item.review ? 1 : 0.5
                                    } else checklist.completed += 1 // Not required to be reviewed
                                }

                                break

                            default:
                                break
                        }
                        if (![FORMAT.LINK, FORMAT.RESOURCE].includes(item.type)) {
                            checklist.total++
                        }
                    }
                })
                if (typeof id !== 'undefined') {
                    checklist.score = Math.round((parseFloat(checklist.completed) * 100) / parseInt(checklist.total))
                    // if (past_score != -1 && past_score < 100 && list.score >= 100) {
                    //     dispatch('complete', { checklist: list.id, complete_date: moment().unix(), values: [] })
                    //     list.complete_date = moment()
                    // } else if (past_score != -1 && past_score >= 100 && list.score < 100) {
                    //     dispatch('uncomplete', { checklist: list.id })
                    //     list.complete_date = false
                    // }
                }

                checklists[checklist.id] = { ...checklist }
            }
        })

        // ORDER
        Object.keys(checklists).map(function (item_id, i) {
            let list = JSON.parse(JSON.stringify(checklists[item_id]))
            list.items = buildHierarchicalOrderedItems(list.items)
            checklists[item_id] = list
        })

        // Vue.set(state, "items", { ...state.items });
        commit('setScore', { items: checklists })
    },
    loadAllByState({ state, commit, rootState }, params) {
        // TODO RETURN ISSUES BY STATE (WITHOUT CALLING THE API, ONLY VUE)
        // if (fnCheckConnection()) {
        //   // If Internet, call API
        //   let url = "issues/" + params.state;
        //
        //   return api.get(url, {}).then(function(response) {
        //     _.forEach(_.values(response.data), function(value, key) {
        //       db_issues.setItem(value.id, JSON.stringify(value));
        //     });
        //     dispatch("format", { data: response.data });
        //     dispatch("calcScore");
        //   });
        // } else {
        //   var items = [];
        //   var count = 0;
        //   db_issues
        //     .iterate(function(value, key, iterationNumber) {
        //       items.push(JSON.parse(value));
        //       count++;
        //     })
        //     .then(function() {
        //       if (count > 0) {
        //         dispatch("format", { data: items });
        //         dispatch("calcScore");
        //       }
        //     });
        // }
    },
    loadCheckById(context, params) {
        return api.get('checklist/id/' + params.id, {}).then(function (response) {
            let result = {}

            Object.keys(response.data).map(function (tframe_id, i) {
                var tframe = response.data[tframe_id]

                Object.keys(tframe.templates).map(function (item_id, i) {
                    result = tframe.templates[item_id]

                    // Timezone set
                    result.business_date = result.business_date ? (moment.isMoment(result.business_date) ? result.business_date : moment(result.business_date * 1000)) : false
                    result.created_date = result.created_date ? (moment.isMoment(result.created_date) ? result.created_date : moment(result.created_date * 1000)) : false
                    result.complete_date = result.complete_date ? (moment.isMoment(result.complete_date) ? result.complete_date : moment(result.complete_date * 1000)) : false
                })
            })
            return result
        })
    },
    recoverValues(context, params) {
        // TODO APPEND
        return api.post('checklist/recover/' + params.check, {}).then(function (response) {
            if (response.status) {
                context.dispatch('format', { data: response.data })
                context.dispatch('calcScore', params.id)
            } else {
                router.go()
            }

            return response.data
        })
    },
    syncAction(context, params) {
        params.queued_date = moment().valueOf()
        params.id = moment().valueOf()
        let queue = 'addIssueToQueue'

        if (params.toolQueue) {
            const options = {
                tasks: 'addTaskToQueue',
                audit: 'addAuditToQueue',
                register: 'addRegisterToQueue'
            }

            queue = options[params.toolQueue]
        }

        return context.dispatch(`checklist/${queue}`, params, { root: true }).then(() => {
            return { status: true }
        })
    },
    sync(context, queue) {
        let checklists = {}
        for (let key in queue) {
            let queueElement = queue[key]
            if (typeof checklists[queueElement.checklist] === 'undefined') {
                checklists[queueElement.checklist] = {
                    items: [],
                    resume: { it: 0, is: 0, a: 0, n: 0 },
                    total: 0,
                    answered: 0,
                    score: 0
                }
            }

            checklists[queueElement.checklist].resume = queueElement.resume
            checklists[queueElement.checklist].total = queueElement.total
            checklists[queueElement.checklist].answered = queueElement.answered
            checklists[queueElement.checklist].score = queueElement.score
            checklists[queueElement.checklist].items.push(queueElement)
        }
        return api
            .post(`checklist/${state.type}/sync`, { checklists: JSON.stringify(checklists) })
            .then(function (response) {
                let response2 = JSON.parse(JSON.stringify(response.data)) ? JSON.parse(JSON.stringify(response.data)) : false
                if (response2 && response2.length > 0) {
                    let title = response2 ? response2.at(-1).customized_title : false
                    let check = response2 ? response2.at(-1).checklist : false
                    // if set, update custom title:
                    /* if (title) {
                        context.commit('setTitle', {
                            check: check,
                            title: title
                        })
                    } */
                    context.dispatch('craftCustomTitle', { check: check })
                }
                if (!response.status) {
                    throw new Error('API error') //Por si falla algo de email
                }
                return context
                    .dispatch('updateOfflineDatabase', response)
                    .then(() => {
                        return response
                    })
                    .catch(function (error) {
                        logError(error)
                        return false
                    })
            })
            .catch(function (error) {
                logError(error)
                return false
            })
    },
    async mergeOffline({ state, context, commit, dispatch, rootState }, params = {}) {
        await db_issues_queue.iterate(function (answer, checklistId) {
            if (typeof params.data[answer.checklist] !== 'undefined' && typeof params.data[answer.checklist].items[answer.task] !== 'undefined') {
                params.data[answer.checklist].items[answer.task].employee = answer.employee
                params.data[answer.checklist].items[answer.task].complete_date = answer.complete_date
                params.data[answer.checklist].items[answer.task].value = answer.value ? answer.value.toString() : ''
                params.data[answer.checklist].items[answer.task].requireSync = true
            }
        })
        return params
    },
    async clearOfflineQueue(context, params) {
        const data = params.data
        const action = params.action
        //['answer', 'reset', 'validate', 'unvalidate', 'complete', 'uncomplete']
        const mapActionsToRemove = {
            answer: ['answer', 'reset'],
            reset: ['answer', 'reset'],
            validate: ['validate', 'unvalidate'],
            unvalidate: ['validate', 'unvalidate']
        }

        const actionsToRemove = mapActionsToRemove[action]

        await db_issues_queue.iterate(async function (answer, key, iterationNumber) {
            const isTheSameChecklist = answer.checklist === data.checklist && answer.task === data.task
            if (actionsToRemove.includes(answer.action) && isTheSameChecklist) {
                await db_issues_queue.removeItem(key)
            }
        })
    },
    async updateOfflineDatabase(context, response) {
        if (response.data) {
            //if(response.data.type && response.data.type === 'complete') return

            const checklists = response.data
            checklists.forEach(async (checklist) => {
                //Actualizar el estado de sincronización de los registros
                await context.dispatch('updateStateOfSync', checklist)

                // Actualizar el id de sincronización de los registros
                if (checklist && checklist.checklist_temporal && checklist.checklist) {
                    const register = await db_issues.getItem(checklist.checklist_temporal)
                    if (register) {
                        register.id = checklist.checklist
                        register.issue_id = checklist.issue_id
                        await db_issues.removeItem(checklist.checklist_temporal)
                        await db_issues.setItem(checklist.checklist, register)
                    }
                    //change the key of the item and id
                    const copy = { ...context.state.items }
                    copy[checklist.checklist_temporal].id = checklist.checklist
                    copy[checklist.checklist_temporal].issue_id = checklist.issue_id
                    copy[checklist.checklist_temporal].temporal_id = checklist.checklist_temporal
                    const updateItem = { [checklist.checklist]: copy[checklist.checklist_temporal] }
                    delete copy[checklist.checklist_temporal]

                    const allItems = { ...updateItem, ...copy }
                    context.state.items = { ...copy }
                    await context.dispatch('format', { data: allItems })

                    //update allItems
                    const newIssueItem = context.state.items[checklist.checklist]
                    delete context.state.allItems[newIssueItem.status][checklist.checklist_temporal]
                    context.state.allItems[newIssueItem.status] = { [checklist.checklist]: newIssueItem, ...context.state.allItems[newIssueItem.status] }
                }
            })
        }
    },

    async updateStateOfSync(context, params) {
        // Actualizar el estado de sincronización de los registros
        if (params && params.checklist_temporal) {
            const register = await db_issues.getItem(params.checklist_temporal)
            if (register) {
                register.requireSync = false
                await db_issues.setItem(params.checklist_temporal, register)
            }
            state.items[params.checklist_temporal].requireSync = false
        }
    }
}

// mutations
const mutations = {
    resetState(state) {
        Object.assign(state, getDefaultState())
    },
    setRadarKPIs(state, radarKPIs) {
        state.radarKPIs = radarKPIs
    },
    updateOpenIssuesRadarKPI(state, { templateId, delta }) {
        const numericDelta = Number(delta)

        if (templateId == null || !numericDelta) {
            return
        }

        const baseRadarKPIs = Array.isArray(state.radarKPIs) ? state.radarKPIs : []
        const kpiIndex = baseRadarKPIs.findIndex((rawKpi) => rawKpi && rawKpi.key === 'open_issues')

        if (kpiIndex === -1) {
            if (numericDelta < 0) {
                return
            }

            Vue.set(state, 'radarKPIs', [
                ...baseRadarKPIs,
                {
                    key: 'open_issues',
                    templates: [{ template_id: templateId, value: numericDelta }],
                    severity: 'warning'
                }
            ])
            return
        }

        const rawKpi = baseRadarKPIs[kpiIndex]
        const templates = Array.isArray(rawKpi.templates) ? [...rawKpi.templates] : []
        const templateIndex = templates.findIndex((templateEntry) => {
            const currentTemplateId = templateEntry && typeof templateEntry === 'object' ? (templateEntry.template_id != null ? templateEntry.template_id : templateEntry.id) : templateEntry

            return currentTemplateId != null && String(currentTemplateId) === String(templateId)
        })

        if (templateIndex === -1) {
            if (numericDelta < 0) {
                return
            }

            templates.push({ template_id: templateId, value: numericDelta })
        } else {
            const templateEntry = templates[templateIndex]
            const currentValue = Number(templateEntry && templateEntry.value) || 0
            const nextValue = Math.max(currentValue + numericDelta, 0)

            if (nextValue === 0) {
                templates.splice(templateIndex, 1)
            } else {
                templates[templateIndex] = {
                    ...(templateEntry && typeof templateEntry === 'object' ? templateEntry : {}),
                    template_id: templateEntry && templateEntry.template_id != null ? templateEntry.template_id : templateId,
                    value: nextValue
                }
            }
        }

        const nextRadarKPIs = [...baseRadarKPIs]
        nextRadarKPIs[kpiIndex] = { ...rawKpi, templates }
        Vue.set(state, 'radarKPIs', nextRadarKPIs)
    },
    setNumberOpenIssues(state, payload) {
        Vue.set(state, 'numberOpenIssues', payload)
    },
    setCompleteDateItem(state, payload) {
        var issueObject = { ...state.allItems[payload.check][payload.item] }
        let allItems = { ...state.allItems }
        if (!allItems[payload.new_status]) {
            allItems[payload.new_status] = {}
        }
        issueObject.complete_date = payload.complete_date
        issueObject.status = payload.new_status
        if (payload.employee) {
            issueObject.employee = payload.employee
        }
        allItems[payload.new_status][payload.item] = { ...issueObject }
        Vue.set(state, 'allItems', { ...allItems })

        allItems = { ...state.allItems }
        delete allItems[payload.check][payload.item]

        Vue.set(state, 'allItems', { ...allItems })
        issueObject = { ...state.items[payload.item] }
        issueObject.complete_date = payload.complete_date
        if (payload.employee) {
            issueObject.employee = payload.employee
        }

        Vue.set(state.items, payload.item, { ...issueObject })
    },
    resetAllItems(state) {
        state.allItems = {}
        Vue.set(state, 'allItems', {})
    },
    setCategories(state, payload) {
        var categories = state.categories
        categories = payload
        Vue.set(state, 'categories', { ...categories })
    },
    setDate(state, date) {
        Vue.set(state, 'date', date)
    },
    setTemplates(state, payload) {
        var templates = {}

        if (payload.data != undefined) {
            Object.keys(payload.data).map(function (tpl_id, i) {
                var tpl = { ...payload.data[tpl_id] }

                tpl.last_update = tpl.last_update ? (moment.isMoment(tpl.last_update) ? tpl.last_update : moment(tpl.last_update * 1000)) : false

                templates[tpl.id] = tpl
            })
        }

        Vue.set(state, 'templates', { ...templates })
    },
    setTemplatesLoaded(state, payload) {
        state.templatesLoaded = payload
    },
    setChecklistProp(state, payload) {
        var check = payload.check
        var prop = payload.prop
        var value = payload.value

        state.items[check][prop] = value
        Vue.set(state, 'items', { ...state.items })
    },
    setAnswerProp(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var prop = payload.prop
        var value = payload.value

        state.items[check].items[answer][prop] = value

        Vue.set(state, 'items', { ...state.items })
    },
    setComment(state, payload) {
        let check = payload.check
        let answer = payload.answer
        let aux = state.items[check].items[answer]

        aux.message = payload.message
        aux.image = payload.image ? payload.image : false
        aux.requireSync = payload.requireSync

        Vue.set(state.items[check].items, answer, { ...aux })
        Vue.set(state, 'items', { ...state.items })
    },
    setAnswer(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var value = payload.value
        var date = payload.date
        var bReadingFromSensor = payload.bReadingFromSensor

        state.items[check].items[answer].value = value
        state.items[check].items[answer].requireSync = payload.requireSync
        state.items[check].items[answer].readingFromSensor = bReadingFromSensor
        if (date) {
            var item = state.items[check].items[answer]
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) state.items[check].items[answer].employee = emp

        Vue.set(state, 'items', { ...state.items })
    },
    setTitle(state, payload) {
        state.items[payload.check].title = payload.title
        state.allItems[payload.status][payload.check].title = payload.title

        Vue.set(state, 'items', { ...state.items })
    },
    setApply(state, payload) {
        const check = payload.check
        const answer = payload.answer
        const emp = payload.emp
        const apply = payload.apply
        const date = payload.date

        state.items[check].items[answer].apply = apply

        if (date) {
            state.items[check].items[answer].complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
            state.items[check].items[answer].value = null
        } else {
            state.items[check].items[answer].complete_date = false
        }

        if (emp) {
            state.items[check].items[answer].employee = emp
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setReset(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var emp = payload.emp ? payload.emp : false
        var date = payload.date ? payload.date : false

        state.items[check].items[answer].complete_date = false
        state.items[check].items[answer].employee = false
        state.items[check].items[answer].review = false
        state.items[check].items[answer].value = null

        if (emp && date) {
            state.items[check].items[answer].reset = {
                e: emp,
                d: date
            }
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setValidation(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var date = payload.date

        state.items[check].items[answer].review = {}
        if (date) {
            var item = state.items[check].items[answer].review
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) {
            var employee = store.getters['employee/getEmployee'](emp)
            state.items[check].items[answer].review.employee = employee
        }
        state.items[check].items[answer].review.value = 1

        Vue.set(state, 'items', { ...state.items })
    },
    setUnvalidation(state, payload) {
        var check = payload.check
        var answer = payload.answer

        state.items[check].items[answer].review = false

        Vue.set(state, 'items', { ...state.items })
    },
    resetStats(state, payload) {
        state.stats.alerts = {}
        state.stats.activity = {}
    },
    setStats(state, payload) {
        var stats = state.stats
        if (typeof stats[payload.where] === 'undefined') stats[payload.where] = {}
        stats[payload.where][payload.key_name] = payload.values
        state.stats = stats
    },
    setFormat(state, payload) {
        state.items = payload.items
        Vue.set(state, 'items', { ...payload.items })
    },
    setAllItems(state, payload) {
        let items = { ...state.allItems }
        if (payload.type) {
            items[payload.type] = payload.items
        } else {
            for (var key in payload.items) {
                const item = payload.items[key]
                if (!items[item.status]) {
                    items[item.status] = {}
                }
                items[item.status][key] = item
            }
        }

        Vue.set(state, 'allItems', { ...items })
    },
    setScore(state, payload) {
        state.items = payload.items
        Vue.set(state, 'items', { ...payload.items })
    },
    filterByState(state, payload) {
        let aItems = state.items

        Object.keys(aItems).map(function (item_id, i) {
            var item = aItems[item_id]

            if (payload.states.includes(item.status) || !payload.states.length) {
                item.is_visible = true
            } else {
                item.is_visible = false
            }
        })

        state.items = aItems
        Vue.set(state, 'items', { ...aItems })
    },
    setRequireSync(state, payload) {
        let check = payload.check
        let answer = payload.answer
        let apiId = payload.apiId

        check = state.items[check] ? check : state.items[apiId] ? apiId : false
        if (answer && typeof state.items[check] !== 'undefined' && typeof state.items[check].items[answer] !== 'undefined') {
            const copyOfItem = { ...state.items }
            copyOfItem[check].items[answer].requireSync = payload.requireSync
            copyOfItem[check].items[answer] = { ...copyOfItem[check].items[answer] }
            Vue.set(state, 'items', { ...copyOfItem })
            state.items = { ...copyOfItem }
        }
    }
}

function loadOffline(context) {
    let templates = []
    let count = 0

    return db_templates
        .iterate(function (value, key, iterationNumber) {
            value = typeof value === 'string' ? JSON.parse(value) : value
            if (value.type == state.type) {
                templates.push(value)
                count++
            }
        })
        .then(function () {
            if (count > 0) {
                context.commit('setTemplates', { data: templates })
            }
            // Se marca como cargado siempre, incluso sin templates locales: distingue "sin plantillas" de "cargando".
            context.commit('setTemplatesLoaded', true)
        })
}

function loadDetailOffline(context, id) {
    return db_issues.getItem(id).then(function (value) {
        if (value) {
            context.dispatch('format', { data: { [id]: { ...value } } })
            context.dispatch('calcScore', id)
        }
    })
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
